import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Line, Doughnut, Pie } from 'react-chartjs-2';
import { resultsService, FilterResultsDto } from '../services/results.service';
import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

interface DashboardContext {
  selectedRound: string | null;
}

const Dashboard: React.FC = () => {
  const { selectedRound } = useOutletContext<DashboardContext>();
  const [filters, setFilters] = useState<FilterResultsDto>({
    round: selectedRound || undefined,
    period: undefined,
    company: undefined,
    team: undefined,
  });
  const [stats, setStats] = useState<any>(null);
  const [availableCompanies, setAvailableCompanies] = useState<string[]>([]);
  const [availableTeams, setAvailableTeams] = useState<string[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [trendData, setTrendData] = useState<any>(null); // 전체 선택 시 트랜드 데이터
  const [isLoading, setIsLoading] = useState(false);
  const [spamReporters, setSpamReporters] = useState<any[]>([]);
  const [isLoadingSpamReporters, setIsLoadingSpamReporters] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadPeriods();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.round]);

  useEffect(() => {
    if (filters.period === 'all') {
      loadTrendData();
      setSpamReporters([]);
    } else {
      loadStats();
      loadSpamReporters();
      setTrendData(null);
    }
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (filters.company) {
      loadTeams();
    } else {
      setAvailableTeams([]);
      setFilters((prev) => ({ ...prev, team: undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.company, filters.round, filters.period]);

  const loadPeriods = async () => {
    try {
      const periods = await resultsService.getAvailablePeriods();
      setAvailablePeriods(periods);
    } catch (error) {
      console.error('Failed to load periods:', error);
    }
  };

  const loadData = async () => {
    try {
      const companies = await resultsService.getAvailableCompanies(
        filters.round || undefined,
        filters.period && filters.period !== 'all' ? filters.period : undefined
      );
      setAvailableCompanies(companies);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const companies = await resultsService.getAvailableCompanies(
        filters.round || undefined,
        filters.period && filters.period !== 'all' ? filters.period : undefined
      );
      setAvailableCompanies(companies);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadTeams = async () => {
    try {
      const teams = await resultsService.getAvailableTeams(
        filters.round || undefined,
        filters.company,
        filters.period && filters.period !== 'all' ? filters.period : undefined
      );
      setAvailableTeams(teams);
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  };

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const data = await resultsService.getStats(filters);
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSpamReporters = async () => {
    if (filters.period === 'all') {
      setSpamReporters([]);
      return;
    }
    setIsLoadingSpamReporters(true);
    try {
      const data = await resultsService.getSpamReporters(filters);
      setSpamReporters(data);
    } catch (error) {
      console.error('Failed to load spam reporters:', error);
      setSpamReporters([]);
    } finally {
      setIsLoadingSpamReporters(false);
    }
  };

  // 전체 선택 시 트랜드 데이터 로드 (각 period별 통계)
  const loadTrendData = async () => {
    setIsLoading(true);
    try {
      // availablePeriods가 없으면 먼저 로드
      let periods = availablePeriods;
      if (periods.length === 0) {
        periods = await resultsService.getAvailablePeriods();
        setAvailablePeriods(periods);
      }
      
      const trendStats: Record<string, any> = {};
      
      // 각 period별로 통계 조회
      for (const period of periods) {
        const periodFilters = { ...filters, period };
        const data = await resultsService.getStats(periodFilters);
        trendStats[period] = data;
      }
      
      setTrendData(trendStats);
    } catch (error) {
      console.error('Failed to load trend data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key: keyof FilterResultsDto, value: string | undefined) => {
    if (key === 'company') {
      setFilters({ ...filters, company: value, team: undefined });
    } else if (key === 'period') {
      setFilters({ ...filters, period: value });
    } else {
      setFilters({ ...filters, [key]: value });
    }
  };

  const handleExportClick = async () => {
    // 비밀번호 입력 기능 활성화
    setShowPasswordModal(true);
    setPassword('');
  };

  const handlePasswordSubmit = async () => {
    // 비밀번호 검증 (4자리 숫자)
    if (!password || password.length !== 4 || !/^\d{4}$/.test(password)) {
      alert('비밀번호는 4자리 숫자여야 합니다.');
      return;
    }

    setIsExporting(true);
    try {
      const blob = await resultsService.exportToExcel(filters, password);
      
      // 파일명 생성 (그래프 타이틀과 동일)
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${getChartTitle()}_${date}.xlsx`;
      
      // 파일 다운로드
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setShowPasswordModal(false);
      setPassword('');
    } catch (error: any) {
      console.error('Export failed:', error);
      alert('엑셀 다운로드에 실패했습니다: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsExporting(false);
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setPassword('');
  };

  // 차트 제목 생성
  const getChartTitle = () => {
    const parts: string[] = [];
    
    if (filters.period && filters.period !== 'all') {
      parts.push(`${filters.period.slice(0, 4)}년 ${filters.period.slice(4)}월`);
    } else if (filters.period === 'all') {
      parts.push('전체 기간');
    }
    
    if (filters.round) {
      parts.push(filters.round);
    }
    
    if (filters.company && filters.company !== '') {
      parts.push(filters.company);
    }
    
    if (filters.team && filters.team !== '') {
      parts.push(filters.team);
    }
    
    return parts.length > 0 ? parts.join(' - ') : '전체';
  };

  // 제목 길이에 따라 폰트 크기 계산
  const getTitleFontSize = () => {
    const title = getChartTitle();
    const length = title.length;
    
    // 길이에 따라 폰트 크기 조정
    if (length > 30) {
      return '1rem';
    } else if (length > 20) {
      return '1.1rem';
    } else {
      return '1.2rem';
    }
  };

  // 정보입력 데이터가 있는지 확인
  const hasInfoInput = stats && stats.infoInput > 0;

  // 기본 stats 값 (데이터가 없을 때)
  const defaultStats = {
    total: 0,
    unread: 0,
    emailOpened: 0,
    linkClicked: 0,
    infoInput: 0,
    spamReport: 0,
    phishing: 0,
    spam: 0,
  };

  const currentStats = stats || defaultStats;
  const hasSpamReport = currentStats.spamReport > 0;

  // 막대 그래프용: 각 항목을 별도 데이터셋으로 구성, 스팸신고는 스택막대
  const barLabels = hasInfoInput 
    ? ['미열람', '메일열람', '링크클릭', '정보입력', '스팸신고']
    : ['미열람', '메일열람', '링크클릭', '스팸신고'];
  
  const barData = {
    labels: barLabels,
    datasets: [
      // 미열람 (퍼플 파스텔톤 - 채도 높임)
      {
        label: '미열람',
        data: hasInfoInput
          ? [currentStats.unread, 0, 0, 0, 0]
          : [currentStats.unread, 0, 0, 0],
        backgroundColor: 'rgba(156, 100, 200, 0.85)', // 진한 파스텔 퍼플
        borderColor: 'rgba(156, 100, 200, 1)',
        borderWidth: 1,
      },
      // 메일열람 (노란색 파스텔톤 - 채도 높임)
      {
        label: '메일열람',
        data: hasInfoInput
          ? [0, currentStats.emailOpened, 0, 0, 0]
          : [0, currentStats.emailOpened, 0, 0],
        backgroundColor: 'rgba(255, 220, 100, 0.85)', // 진한 파스텔 노란색
        borderColor: 'rgba(255, 220, 100, 1)',
        borderWidth: 1,
      },
      // 링크클릭 (적색 파스텔톤 - 채도 높임)
      {
        label: '링크클릭',
        data: hasInfoInput
          ? [0, 0, currentStats.linkClicked, 0, 0]
          : [0, 0, currentStats.linkClicked, 0],
        backgroundColor: 'rgba(255, 120, 120, 0.85)', // 진한 파스텔 적색
        borderColor: 'rgba(255, 120, 120, 1)',
        borderWidth: 1,
      },
      // 정보입력 (있는 경우만)
      ...(hasInfoInput ? [{
        label: '정보입력',
        data: [0, 0, 0, currentStats.infoInput, 0],
        backgroundColor: 'rgba(46, 204, 113, 0.85)',
        borderColor: 'rgba(46, 204, 113, 1)',
        borderWidth: 1,
      }] : []),
      // 스팸신고 스택막대 (피싱 - 녹색 파스텔톤 - 채도 높임)
      {
        label: '피싱',
        data: hasInfoInput
          ? [0, 0, 0, 0, currentStats.phishing]
          : [0, 0, 0, currentStats.phishing],
        backgroundColor: 'rgba(100, 200, 180, 0.85)', // 진한 파스텔 녹색
        borderColor: 'rgba(100, 200, 180, 1)',
        borderWidth: 1,
      },
      // 스팸신고 스택막대 (스팸 - 파란색 파스텔톤 - 채도 높임)
      {
        label: '스팸',
        data: hasInfoInput
          ? [0, 0, 0, 0, currentStats.spam]
          : [0, 0, 0, currentStats.spam],
        backgroundColor: 'rgba(120, 180, 240, 0.85)', // 진한 파스텔 파란색
        borderColor: 'rgba(120, 180, 240, 1)',
        borderWidth: 1,
      },
    ],
  };

  // 파이/도넛 차트용: 비율 표시를 위해 중복 제거 (메일열람만 = 전체 - 미열람 - 링크클릭 - 스팸신고)
  const emailOpenedOnly = Math.max(0, currentStats.emailOpened - currentStats.linkClicked - currentStats.spamReport);

  const pieLabels = hasInfoInput
    ? ['미열람', '메일열람만', '링크클릭', '스팸신고', '정보입력']
    : ['미열람', '메일열람만', '링크클릭', '스팸신고'];

  // 파이/도넛 차트 색상 (파스텔톤 - 채도 높임)
  const pieChartColors = {
    unread: 'rgba(156, 100, 200, 0.85)', // 미열람 - 진한 파스텔 퍼플
    emailOpened: 'rgba(255, 220, 100, 0.85)', // 메일열람만 - 진한 파스텔 노란색
    linkClicked: 'rgba(255, 120, 120, 0.85)', // 링크클릭 - 진한 파스텔 적색
    spamReport: 'rgba(120, 180, 240, 0.85)', // 스팸신고 - 진한 파스텔 파란색
    infoInput: 'rgba(46, 204, 113, 0.85)', // 정보입력
  };
  const pieChartBorderColors = {
    unread: 'rgba(156, 100, 200, 1)',
    emailOpened: 'rgba(255, 220, 100, 1)',
    linkClicked: 'rgba(255, 120, 120, 1)',
    spamReport: 'rgba(120, 180, 240, 1)',
    infoInput: 'rgba(46, 204, 113, 1)',
  };

  const pieData = {
    labels: pieLabels,
    datasets: [
      {
        data: hasInfoInput
          ? [
              currentStats.unread, 
              emailOpenedOnly, 
              currentStats.linkClicked, 
              currentStats.spamReport,
              currentStats.infoInput
            ]
          : [
              currentStats.unread, 
              emailOpenedOnly, 
              currentStats.linkClicked,
              currentStats.spamReport
            ],
        backgroundColor: hasInfoInput
          ? [
              pieChartColors.unread,
              pieChartColors.emailOpened,
              pieChartColors.linkClicked,
              pieChartColors.spamReport,
              pieChartColors.infoInput,
            ]
          : [
              pieChartColors.unread,
              pieChartColors.emailOpened,
              pieChartColors.linkClicked,
              pieChartColors.spamReport,
            ],
        borderColor: hasInfoInput
          ? [
              pieChartBorderColors.unread,
              pieChartBorderColors.emailOpened,
              pieChartBorderColors.linkClicked,
              pieChartBorderColors.spamReport,
              pieChartBorderColors.infoInput,
            ]
          : [
              pieChartBorderColors.unread,
              pieChartBorderColors.emailOpened,
              pieChartBorderColors.linkClicked,
              pieChartBorderColors.spamReport,
            ],
        borderWidth: 2,
      },
    ],
  };

  const doughnutData = {
    labels: pieLabels,
    datasets: [
      {
        data: hasInfoInput
          ? [
              currentStats.unread, 
              emailOpenedOnly, 
              currentStats.linkClicked, 
              currentStats.spamReport,
              currentStats.infoInput
            ]
          : [
              currentStats.unread, 
              emailOpenedOnly, 
              currentStats.linkClicked,
              currentStats.spamReport
            ],
        backgroundColor: hasInfoInput
          ? [
              pieChartColors.unread,
              pieChartColors.emailOpened,
              pieChartColors.linkClicked,
              pieChartColors.spamReport,
              pieChartColors.infoInput,
            ]
          : [
              pieChartColors.unread,
              pieChartColors.emailOpened,
              pieChartColors.linkClicked,
              pieChartColors.spamReport,
            ],
        borderColor: hasInfoInput
          ? [
              pieChartBorderColors.unread,
              pieChartBorderColors.emailOpened,
              pieChartBorderColors.linkClicked,
              pieChartBorderColors.spamReport,
              pieChartBorderColors.infoInput,
            ]
          : [
              pieChartBorderColors.unread,
              pieChartBorderColors.emailOpened,
              pieChartBorderColors.linkClicked,
              pieChartBorderColors.spamReport,
            ],
        borderWidth: 2,
      },
    ],
  };

  // 막대 그래프 옵션
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 0, // 범례를 그래프 상단에 최대한 가깝게
        bottom: 10,
        left: 10,
        right: 10,
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
        display: true,
        align: 'start' as const, // 범례를 왼쪽 정렬
        labels: {
          boxWidth: 12,
          padding: 4, // 범례 간격 줄이기
          font: {
            size: 11,
          },
          generateLabels: (chart: any) => {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            // 스팸을 제거하고 피싱을 스팸신고로 변경
            return labels
              .filter((label: any) => label.text !== '스팸')
              .map((label: any) => {
                if (label.text === '피싱') {
                  return { ...label, text: '스팸신고' };
                }
                return label;
              });
          },
        },
      },
      title: {
        display: false, // 제목 제거
      },
      datalabels: {
        anchor: (ctx: any) => {
          // 기본적으로 막대 밖에 표시, 차트 영역을 벗어나면 막대 안에 표시
          const chart = ctx.chart;
          const meta = chart.getDatasetMeta(ctx.datasetIndex);
          const bar = meta.data[ctx.dataIndex];
          if (!bar) return 'end';
          
          const yScale = chart.scales.y;
          const chartAreaTop = chart.chartArea.top;
          const chartAreaBottom = chart.chartArea.bottom;
          
          // 막대의 상단 위치 계산
          const barValue = ctx.dataset.data[ctx.dataIndex];
          if (barValue === 0) return 'end';
          
          const stackedValue = ctx.chart.data.datasets
            .slice(0, ctx.datasetIndex + 1)
            .reduce((sum: number, ds: any) => sum + (ds.data[ctx.dataIndex] || 0), 0);
          
          const barTopPixel = yScale.getPixelForValue(stackedValue);
          const barBottomPixel = yScale.getPixelForValue(stackedValue - barValue);
          
          // 텍스트 높이를 고려한 여유 공간 (약 50px)
          const textHeight = 50;
          
          // 차트 영역 상단을 벗어나면 막대 안에 표시
          if (barTopPixel < chartAreaTop + textHeight) {
            return 'center';
          }
          // 차트 영역 하단을 벗어나면 막대 안에 표시
          if (barBottomPixel > chartAreaBottom - 30) {
            return 'center';
          }
          return 'end';
        },
        align: (ctx: any) => {
          const chart = ctx.chart;
          const meta = chart.getDatasetMeta(ctx.datasetIndex);
          const bar = meta.data[ctx.dataIndex];
          if (!bar) return 'top';
          
          const yScale = chart.scales.y;
          const chartAreaTop = chart.chartArea.top;
          const chartAreaBottom = chart.chartArea.bottom;
          
          const barValue = ctx.dataset.data[ctx.dataIndex];
          if (barValue === 0) return 'top';
          
          const stackedValue = ctx.chart.data.datasets
            .slice(0, ctx.datasetIndex + 1)
            .reduce((sum: number, ds: any) => sum + (ds.data[ctx.dataIndex] || 0), 0);
          
          const barTopPixel = yScale.getPixelForValue(stackedValue);
          const barBottomPixel = yScale.getPixelForValue(stackedValue - barValue);
          
          const textHeight = 50;
          
          // 차트 영역을 벗어나면 막대 안에 표시
          if (barTopPixel < chartAreaTop + textHeight || barBottomPixel > chartAreaBottom - 30) {
            return 'center';
          }
          return 'top';
        },
        offset: (ctx: any) => {
          const chart = ctx.chart;
          const meta = chart.getDatasetMeta(ctx.datasetIndex);
          const bar = meta.data[ctx.dataIndex];
          if (!bar) return 10;
          
          const yScale = chart.scales.y;
          const chartAreaTop = chart.chartArea.top;
          const chartAreaBottom = chart.chartArea.bottom;
          
          const barValue = ctx.dataset.data[ctx.dataIndex];
          if (barValue === 0) return 0;
          
          const stackedValue = ctx.chart.data.datasets
            .slice(0, ctx.datasetIndex + 1)
            .reduce((sum: number, ds: any) => sum + (ds.data[ctx.dataIndex] || 0), 0);
          
          const barTopPixel = yScale.getPixelForValue(stackedValue);
          const barBottomPixel = yScale.getPixelForValue(stackedValue - barValue);
          
          const textHeight = 50;
          
          // 차트 영역을 벗어나면 막대 안에 표시 (offset 0)
          if (barTopPixel < chartAreaTop + textHeight || barBottomPixel > chartAreaBottom - 30) {
            return 0;
          }
          
          // 같은 막대(같은 dataIndex)에 여러 데이터셋이 있는지 확인하여 겹침 방지
          const datasetIndex = ctx.datasetIndex;
          const dataIndex = ctx.dataIndex;
          const allDatasets = ctx.chart.data.datasets;
          
          // 같은 막대에 있는 다른 데이터셋들의 개수 계산
          let activeDatasetsCount = 0;
          for (let i = 0; i < allDatasets.length; i++) {
            if (allDatasets[i].data[dataIndex] > 0) {
              activeDatasetsCount++;
            }
          }
          
          // 여러 데이터셋이 있으면 각각 다른 offset 적용 (스택막대 겹침 방지)
          if (activeDatasetsCount > 1) {
            // 현재 데이터셋의 순서에 따라 offset 조정
            let currentOrder = 0;
            for (let i = 0; i < datasetIndex; i++) {
              if (allDatasets[i].data[dataIndex] > 0) {
                currentOrder++;
              }
            }
            // 각 데이터셋마다 15px씩 간격을 둠
            return 10 + (currentOrder * 15);
          }
          
          return 10;
        },
        formatter: (value: number, ctx: any) => {
          // 값이 0이면 표시하지 않음
          if (value === 0) {
            return '';
          }
          
          // 전체(total)에 대한 비율 계산
          const total = currentStats.total || 1;
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          
          return `${value}\n(${percentage}%)`;
        },
        font: {
          size: 10,
          weight: 'bold' as const,
        },
        color: '#000', // 항상 검은색으로 통일
        padding: {
          top: 3,
          bottom: 3,
          left: 5,
          right: 5,
        },
        textStrokeColor: '#fff',
        textStrokeWidth: 2, // 텍스트 스트로크 두께 증가로 가독성 향상
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
        stacked: true, // 스택막대 그래프
      },
      x: {
        stacked: true, // 스택막대 그래프
      },
    },
  };

  // 파이/도넛 차트 옵션
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false, // h3 제목이 있으므로 차트 내부 제목은 숨김
      },
      datalabels: {
        color: '#000', // 검정색으로 통일
        font: {
          size: 14,
          weight: 'bold' as const,
        },
        textStrokeColor: '#fff', // 흰색 음영
        textStrokeWidth: 2, // 음영 두께
        anchor: (ctx: any) => {
          // 파이/도넛 차트에서 작은 조각은 외부에, 큰 조각은 내부에 표시
          const value = ctx.dataset.data[ctx.dataIndex];
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const percentage = total > 0 ? (value / total) * 100 : 0;
          
          // 5% 미만이면 외부에 표시 (겹침 방지)
          if (percentage < 5) {
            return 'end';
          }
          // 5% 이상이면 내부에 표시
          return 'center';
        },
        align: (ctx: any) => {
          const value = ctx.dataset.data[ctx.dataIndex];
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const percentage = total > 0 ? (value / total) * 100 : 0;
          
          // 5% 미만이면 외부에 표시
          if (percentage < 5) {
            return 'end';
          }
          // 5% 이상이면 중앙에 표시
          return 'center';
        },
        offset: (ctx: any) => {
          const value = ctx.dataset.data[ctx.dataIndex];
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const percentage = total > 0 ? (value / total) * 100 : 0;
          
          // 5% 미만이면 외부에 표시 (offset 증가)
          if (percentage < 5) {
            return 10;
          }
          // 5% 이상이면 내부에 표시 (offset 0)
          return 0;
        },
        formatter: (value: number, ctx: any) => {
          // 값이 0이면 표시하지 않음
          if (value === 0) {
            return '';
          }
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          
          // 작은 값(2% 미만)은 값만 표시하여 겹침 방지
          const smallPercentage = total > 0 ? (value / total) * 100 : 0;
          if (smallPercentage < 2) {
            return `${value}`;
          }
          
          return `${value}\n(${percentage}%)`;
        },
        clamp: true, // 차트 영역 밖으로 나가지 않도록
        clip: false, // 차트 영역 밖은 잘라내지 않음 (외부 표시를 위해)
        padding: {
          top: 3,
          bottom: 3,
          left: 5,
          right: 5,
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.parsed || context.raw;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value}명 (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="dashboard">
      <h1>악성메일 모의 훈련 결과 대시보드</h1>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="period-select">기간:</label>
          <select
            id="period-select"
            value={filters.period || ''}
            onChange={(e) => handleFilterChange('period', e.target.value || undefined)}
          >
            <option value="">선택하세요</option>
            {availablePeriods.length >= 2 && (
              <option value="all">전체</option>
            )}
            {availablePeriods.map((period) => (
              <option key={period} value={period}>
                {period.slice(0, 4)}년 {period.slice(4)}월
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="company-select">회사:</label>
          <select
            id="company-select"
            value={filters.company || ''}
            onChange={(e) => handleFilterChange('company', e.target.value || undefined)}
          >
            <option value="">전체</option>
            {availableCompanies.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="team-select">팀:</label>
          <select
            id="team-select"
            value={filters.team || ''}
            onChange={(e) => handleFilterChange('team', e.target.value || undefined)}
            disabled={!filters.company}
          >
            <option value="">전체</option>
            {availableTeams.map((team) => (
              <option key={team} value={team}>
                {team}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <button 
            className="export-button" 
            onClick={handleExportClick}
            disabled={isLoading || !stats || isExporting}
          >
            {isExporting ? '다운로드 중...' : '엑셀 다운로드'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">데이터를 불러오는 중...</div>
      ) : filters.period === 'all' && trendData ? (
        // 전체 선택 시 트랜드 분석 그래프
        <TrendAnalysisChart trendData={trendData} availablePeriods={availablePeriods} filters={filters} />
      ) : (
        <>
          <div className="stats-summary">
            <div className="stat-card">
              <div className="stat-label">전체</div>
              <div className="stat-value">{currentStats.total}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">미열람</div>
              <div className="stat-value">{currentStats.unread}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">메일열람</div>
              <div className="stat-value">{currentStats.emailOpened}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">링크클릭</div>
              <div className="stat-value">{currentStats.linkClicked}</div>
            </div>
            {hasInfoInput && (
              <div className="stat-card">
                <div className="stat-label">정보입력</div>
                <div className="stat-value">{currentStats.infoInput}</div>
              </div>
            )}
          </div>

          <div className="charts-container">
            <div className="chart-card">
              <h3 style={{ fontSize: getTitleFontSize() }}>{getChartTitle()}</h3>
              <div className="chart-wrapper">
                <Bar data={barData} options={barOptions} plugins={[ChartDataLabels]} />
              </div>
            </div>

            <div className="chart-card">
              <h3 style={{ fontSize: getTitleFontSize() }}>{getChartTitle()}</h3>
              <div className="chart-wrapper">
                <Pie data={pieData} options={pieOptions} plugins={[ChartDataLabels]} />
              </div>
            </div>

            <div className="chart-card">
              <h3 style={{ fontSize: getTitleFontSize() }}>{getChartTitle()}</h3>
              <div className="chart-wrapper">
                <Doughnut data={doughnutData} options={pieOptions} plugins={[ChartDataLabels]} />
              </div>
            </div>
          </div>

          {/* 스팸신고자 테이블 */}
          {!isLoading && filters.period !== 'all' && (
            <div className="spam-reporters-table" style={{ marginTop: '3rem' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <span>스팸신고자</span>
              </h3>
              {isLoadingSpamReporters ? (
                <div className="loading">데이터를 불러오는 중...</div>
              ) : spamReporters.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>회사</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>팀</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>이름</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>카테고리분류</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spamReporters.map((reporter, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '0.75rem' }}>{reporter.company || '-'}</td>
                        <td style={{ padding: '0.75rem' }}>{reporter.team || '-'}</td>
                        <td style={{ padding: '0.75rem' }}>{reporter.name || '-'}</td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            backgroundColor: reporter.category === '피싱' ? 'rgba(155, 89, 182, 0.1)' : 'rgba(230, 126, 34, 0.1)',
                            color: reporter.category === '피싱' ? 'rgba(155, 89, 182, 1)' : 'rgba(230, 126, 34, 1)',
                          }}>
                            {reporter.category || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#666', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                  스팸신고자가 없습니다.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* 엑셀 다운로드 비밀번호 입력 모달 */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={handlePasswordCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>엑셀 다운로드</h3>
            <p>엑셀 파일을 다운로드합니다.</p>
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                비밀번호 (4자리 숫자):
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                  setPassword(value);
                }}
                placeholder="0000"
                maxLength={4}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  textAlign: 'center',
                  letterSpacing: '0.5rem',
                }}
                autoFocus
              />
            </div>
            <div className="modal-buttons" style={{ marginTop: '1.5rem' }}>
              <button onClick={handlePasswordCancel}>취소</button>
              <button onClick={handlePasswordSubmit} disabled={isExporting || password.length !== 4}>
                {isExporting ? '다운로드 중...' : '다운로드'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 트랜드 분석 차트 컴포넌트
const TrendAnalysisChart: React.FC<{
  trendData: Record<string, any>;
  availablePeriods: string[];
  filters: FilterResultsDto;
}> = ({ trendData, availablePeriods, filters }) => {
  // period별 라벨 생성
  const periodLabels = availablePeriods.map((p) => `${p.slice(0, 4)}년 ${p.slice(4)}월`);

  // 회사별 트랜드 데이터 (회사가 선택되지 않은 경우)
  const companyTrendData = filters.company
    ? null
    : {
        labels: periodLabels,
        datasets: [
          {
            label: '전체',
            data: availablePeriods.map((p) => trendData[p]?.total || 0),
            borderColor: 'rgba(52, 152, 219, 1)',
            backgroundColor: 'rgba(52, 152, 219, 0.2)',
            tension: 0.4,
          },
          {
            label: '미열람',
            data: availablePeriods.map((p) => trendData[p]?.unread || 0),
            borderColor: 'rgba(231, 76, 60, 1)',
            backgroundColor: 'rgba(231, 76, 60, 0.2)',
            tension: 0.4,
          },
          {
            label: '메일열람',
            data: availablePeriods.map((p) => trendData[p]?.emailOpened || 0),
            borderColor: 'rgba(52, 152, 219, 1)',
            backgroundColor: 'rgba(52, 152, 219, 0.2)',
            tension: 0.4,
          },
          {
            label: '링크클릭',
            data: availablePeriods.map((p) => trendData[p]?.linkClicked || 0),
            borderColor: 'rgba(241, 196, 15, 1)',
            backgroundColor: 'rgba(241, 196, 15, 0.2)',
            tension: 0.4,
          },
        ],
      };

  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: filters.company ? `${filters.company} 트랜드 분석` : '전체 트랜드 분석',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <div className="trend-analysis">
      <h2>트랜드 분석</h2>
      {companyTrendData && (
        <div className="chart-card">
          <h3>기간별 트랜드</h3>
          <div className="chart-wrapper" style={{ height: '400px' }}>
            <Line data={companyTrendData} options={trendOptions} plugins={[ChartDataLabels]} />
          </div>
        </div>
      )}
      {filters.company && (
        <div className="chart-card">
          <h3>{filters.company} 트랜드</h3>
          <div className="chart-wrapper" style={{ height: '400px' }}>
            <Line
              data={{
                labels: periodLabels,
                datasets: [
                  {
                    label: '전체',
                    data: availablePeriods.map((p) => trendData[p]?.total || 0),
                    borderColor: 'rgba(52, 152, 219, 1)',
                    backgroundColor: 'rgba(52, 152, 219, 0.2)',
                    tension: 0.4,
                    fill: true,
                  },
                  {
                    label: '미열람',
                    data: availablePeriods.map((p) => trendData[p]?.unread || 0),
                    borderColor: 'rgba(231, 76, 60, 1)',
                    backgroundColor: 'rgba(231, 76, 60, 0.2)',
                    tension: 0.4,
                    fill: true,
                  },
                  {
                    label: '메일열람',
                    data: availablePeriods.map((p) => trendData[p]?.emailOpened || 0),
                    borderColor: 'rgba(46, 204, 113, 1)',
                    backgroundColor: 'rgba(46, 204, 113, 0.2)',
                    tension: 0.4,
                    fill: true,
                  },
                  {
                    label: '링크클릭',
                    data: availablePeriods.map((p) => trendData[p]?.linkClicked || 0),
                    borderColor: 'rgba(241, 196, 15, 1)',
                    backgroundColor: 'rgba(241, 196, 15, 0.2)',
                    tension: 0.4,
                    fill: true,
                  },
                ],
              }}
              options={trendOptions}
              plugins={[ChartDataLabels]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

